import { createReducer } from '@reduxjs/toolkit';
import {
  GET_THERMOSTATS,
  UPDATE_DEMO_THERMOSTAT,
  UPDATE_DEMO_THERMOSTAT_STATE,
  UPDATE_ECOBEE_THERMOSTAT,
  UPDATE_THERMOSTAT,
  UPDATE_THERMOSTAT_HVAC_MODE,
  UPDATE_THERMOSTAT_BULK,
  UPDATE_THERMOSTAT_HVAC_MODE_BULK,
  ATTACH_THERMOSTAT_PRESET,
  GET_BUILDING_THERMOSTAT_PRESETS,
  GET_MASTER_THERMOSTAT_CONTROL,
  UPDATE_MASTER_THERMOSTAT_CONTROL,
  UPDATE_BUILDING_THERMOSTAT_PRESETS,
  CREATE_THERMOSTAT_PRESET
} from '../constants';
import { requestFail, requestPending, requestSuccess } from '../../utils/status';

const initialState = {
  status: '',
  updateEcobeeThermostatStatus: '',
  updateThermostatStatus: '',
  updateThermostatHvacModeStatus: '',
  updateThermostatBulkStatus: '',
  updateThermostatHvacModeBulkStatus: '',
  thermostatData: [],
  search: '',
  filters: {},
  ordering: {},
  pagination: {
    page: 1,
    limit: 20
  },

  // Building thermostat presets
  buildingPresets: [],
  buildingPresetsStatus: '',
  updatePresetStatus: '',
  updateMasterControlStatus: false,
  updatePresetsStatus: '',
  createPresetStatus: '',
  masterControlStatus: false,
  masterThermostatControl: null,
  masterThermostatControlStatus: '',

  // Remove or keep demo variables based on actual usage
  initialized: false,
  enableOccupiedSnapBack: false,
  occupiedSnapBackTemperature: 65,
  occupiedSnapBackTemperatureAdjustment: 3,
  occupiedHeatTemperatureRange: [47, 56],
  occupiedCoolTemperatureRange: [74, 85],
  enableVacantSnapBack: false,
  vacantSnapBackTemperature: 60,
  vacantSnapBackTemperatureAdjustment: 3,
  vacantHeatTemperatureRange: [47, 56],
  vacantCoolTemperatureRange: [74, 85],

  attachPresetStatus: 'None',
};

export default createReducer(initialState, {
  [UPDATE_DEMO_THERMOSTAT]: (state, { payload }) => ({
    ...state,
    thermostatData: payload
  }),

  [UPDATE_DEMO_THERMOSTAT_STATE]: (state, { payload }) => ({
    ...state,
    ...payload
  }),

  [requestSuccess(GET_THERMOSTATS)]: (state, { payload }) => ({
    ...state,
    status: requestSuccess(GET_THERMOSTATS),
    thermostatData: payload
  }),

  [requestPending(GET_THERMOSTATS)]: (state) => ({
    ...state,
    status: requestPending(GET_THERMOSTATS)
  }),

  [requestFail(GET_THERMOSTATS)]: (state) => ({
    ...state,
    status: requestFail(GET_THERMOSTATS),
    thermostatData: null
  }),

  [requestSuccess(UPDATE_ECOBEE_THERMOSTAT)]: (state, { payload }) => ({
    ...state,
    updateEcobeeThermostatStatus: requestSuccess(UPDATE_ECOBEE_THERMOSTAT)
  }),

  [requestPending(UPDATE_ECOBEE_THERMOSTAT)]: (state) => ({
    ...state,
    updateEcobeeThermostatStatus: requestPending(UPDATE_ECOBEE_THERMOSTAT)
  }),

  [requestFail(UPDATE_ECOBEE_THERMOSTAT)]: (state) => ({
    ...state,
    updateEcobeeThermostatStatus: requestFail(UPDATE_ECOBEE_THERMOSTAT)
  }),

  [requestSuccess(GET_BUILDING_THERMOSTAT_PRESETS)]: (state, { payload }) => ({
    ...state,
    buildingPresetsStatus: requestSuccess(GET_BUILDING_THERMOSTAT_PRESETS),
    buildingPresets: payload
  }),

  [requestPending(GET_BUILDING_THERMOSTAT_PRESETS)]: (state) => ({
    ...state,
    buildingPresetsStatus: requestPending(GET_BUILDING_THERMOSTAT_PRESETS)
  }),

  [requestFail(GET_BUILDING_THERMOSTAT_PRESETS)]: (state) => ({
    ...state,
    buildingPresetsStatus: requestFail(GET_BUILDING_THERMOSTAT_PRESETS),
  }),

  [requestSuccess(GET_MASTER_THERMOSTAT_CONTROL)]: (state, { payload }) => ({
    ...state,
    masterThermostatControl: payload,
    masterThermostatControlStatus: requestSuccess(GET_MASTER_THERMOSTAT_CONTROL),
    masterControlStatus: payload?.is_active || false // Set the master control status
  }),

  [requestPending(GET_MASTER_THERMOSTAT_CONTROL)]: (state) => ({
    ...state,
    masterThermostatControlStatus: requestPending(GET_MASTER_THERMOSTAT_CONTROL)
  }),

  [requestFail(GET_MASTER_THERMOSTAT_CONTROL)]: (state) => ({
    ...state,
    masterThermostatControlStatus: requestFail(GET_MASTER_THERMOSTAT_CONTROL)
  }),

  [requestSuccess(UPDATE_MASTER_THERMOSTAT_CONTROL)]: (state, { payload }) => {
    return {
      ...state,
      updateMasterControlStatus: requestSuccess(UPDATE_MASTER_THERMOSTAT_CONTROL),
      masterControlStatus: payload?.is_active || false
    };
  },
  
  [requestPending(UPDATE_MASTER_THERMOSTAT_CONTROL)]: (state) => ({
    ...state,
    updateMasterControlStatus: requestPending(UPDATE_MASTER_THERMOSTAT_CONTROL)
  }),

  [requestFail(UPDATE_MASTER_THERMOSTAT_CONTROL)]: (state) => ({
    ...state,
    updateMasterControlStatus: requestFail(UPDATE_MASTER_THERMOSTAT_CONTROL)
  }),

  [requestSuccess(UPDATE_BUILDING_THERMOSTAT_PRESETS)]: (state, { payload }) => ({
    ...state,
    updatePresetsStatus: requestSuccess(UPDATE_BUILDING_THERMOSTAT_PRESETS),
    buildingPresets: Array.isArray(payload) 
      ? state.buildingPresets.map(preset => {
          const updatedPreset = payload.find(updated => updated.id === preset.id);
          return updatedPreset ? { ...preset, ...updatedPreset } : preset;
        })
      : payload
  }),

  [requestPending(UPDATE_BUILDING_THERMOSTAT_PRESETS)]: (state) => ({
    ...state,
    updatePresetsStatus: requestPending(UPDATE_BUILDING_THERMOSTAT_PRESETS)
  }),

  [requestFail(UPDATE_BUILDING_THERMOSTAT_PRESETS)]: (state) => ({
    ...state,
    updatePresetsStatus: requestFail(UPDATE_BUILDING_THERMOSTAT_PRESETS)
  }),

  [requestSuccess(ATTACH_THERMOSTAT_PRESET)]: (state, action) => ({
      ...state,
      attachPresetStatus: requestSuccess(ATTACH_THERMOSTAT_PRESET),
  }),

  [requestPending(ATTACH_THERMOSTAT_PRESET)]: (state) => ({ 
    ...state,
    attachPresetStatus: requestPending(ATTACH_THERMOSTAT_PRESET)
  }),

  [requestFail(ATTACH_THERMOSTAT_PRESET)]: (state) => ({
    ...state,
    attachPresetStatus: requestFail(ATTACH_THERMOSTAT_PRESET)
  }),

  [requestSuccess(CREATE_THERMOSTAT_PRESET)]: (state, { payload }) => ({
    ...state,
    createPresetStatus: requestSuccess(CREATE_THERMOSTAT_PRESET),
    buildingPresets: [...state.buildingPresets, payload]
  }),

  [requestPending(CREATE_THERMOSTAT_PRESET)]: (state) => ({
    ...state,
    createPresetStatus: requestPending(CREATE_THERMOSTAT_PRESET)
  }),

  [requestFail(CREATE_THERMOSTAT_PRESET)]: (state) => ({
    ...state,
    createPresetStatus: requestFail(CREATE_THERMOSTAT_PRESET)
  }),

  [requestSuccess(UPDATE_THERMOSTAT)]: (state, { payload }) => ({
    ...state,
    updateThermostatStatus: requestSuccess(UPDATE_THERMOSTAT),
    // Update the specific thermostat in the thermostatData array
    thermostatData: state.thermostatData.map(thermostat => 
      thermostat.id === payload.id ? { ...thermostat, ...payload } : thermostat
    )
  }),

  [requestPending(UPDATE_THERMOSTAT)]: (state) => ({
    ...state,
    updateThermostatStatus: requestPending(UPDATE_THERMOSTAT)
  }),

  [requestFail(UPDATE_THERMOSTAT)]: (state) => ({
    ...state,
    updateThermostatStatus: requestFail(UPDATE_THERMOSTAT)
  }),

  [requestSuccess(UPDATE_THERMOSTAT_HVAC_MODE)]: (state, { payload }) => ({
    ...state,
    updateThermostatHvacModeStatus: requestSuccess(UPDATE_THERMOSTAT_HVAC_MODE),
    thermostatData: state.thermostatData.map(thermostat => 
      thermostat.id === payload.id ? { ...thermostat, ...payload } : thermostat
    )
  }),

  [requestPending(UPDATE_THERMOSTAT_HVAC_MODE)]: (state) => ({
    ...state,
    updateThermostatHvacModeStatus: requestPending(UPDATE_THERMOSTAT_HVAC_MODE)
  }),

  [requestFail(UPDATE_THERMOSTAT_HVAC_MODE)]: (state) => ({
    ...state,
    updateThermostatHvacModeStatus: requestFail(UPDATE_THERMOSTAT_HVAC_MODE)
  }),

  [requestSuccess(UPDATE_THERMOSTAT_BULK)]: (state, { payload }) => ({
    ...state,
    updateThermostatBulkStatus: requestSuccess(UPDATE_THERMOSTAT_BULK),
    thermostatData: state.thermostatData.map(thermostat => {
      const updatedThermostat = payload.find(updated => updated.id === thermostat.id);
      return updatedThermostat ? { ...thermostat, ...updatedThermostat } : thermostat;
    })
  }),

  [requestPending(UPDATE_THERMOSTAT_BULK)]: (state) => ({
    ...state,
    updateThermostatBulkStatus: requestPending(UPDATE_THERMOSTAT_BULK)
  }),

  [requestFail(UPDATE_THERMOSTAT_BULK)]: (state) => ({
    ...state,
    updateThermostatBulkStatus: requestFail(UPDATE_THERMOSTAT_BULK)
  }),

  [requestSuccess(UPDATE_THERMOSTAT_HVAC_MODE_BULK)]: (state, { payload }) => ({
    ...state,
    updateThermostatHvacModeBulkStatus: requestSuccess(UPDATE_THERMOSTAT_HVAC_MODE_BULK),
    thermostatData: state.thermostatData.map(thermostat => {
      const updatedThermostat = payload.find(updated => updated.id === thermostat.id);
      return updatedThermostat ? { ...thermostat, ...updatedThermostat } : thermostat;
    })
  }),
  
  [requestPending(UPDATE_THERMOSTAT_HVAC_MODE_BULK)]: (state) => ({
    ...state,
    updateThermostatHvacModeBulkStatus: requestPending(UPDATE_THERMOSTAT_HVAC_MODE_BULK)
  }),

  [requestFail(UPDATE_THERMOSTAT_HVAC_MODE_BULK)]: (state) => ({
    ...state,
    updateThermostatHvacModeBulkStatus: requestFail(UPDATE_THERMOSTAT_HVAC_MODE_BULK)
  })
}
);
